import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import { merge, $ } from "glamor";
import theme from "styles/theme";
import CircularProgress from "material-ui/CircularProgress";
import { ItemsHeader } from "./items-header";
import ItemGrid from "components/items/grid";
import { ItemsList } from "components/items/items-list";
import Filters from "components/filters";
import { StickyContainer, Sticky } from "react-sticky";
import BulkActions from "components/items/bulk-actions";
import NotesDialog from "components/items/item/notes";
import DatepickerIcon from "material-ui/svg-icons/action/date-range";
import Tooltip from "material-ui/internal/Tooltip";
import { VIEWPORT_PHABLET, VIEWPORT_MOBILE } from "constants/media";
import { DATATEST } from "constants/integration";
import me from "models/me";
import { formatMessage } from "lib/globalize-util";
import { EmptyResults, isResultEmpty } from "../empty-results";
import BulkManager from "./bulk-manager";

const ErrorMessage = ({ error }) => {
    const errorStyle = {
        padding: "8px 0 0",
        height: "64px",
        lineHeight: "56px",
        fontSize: "20px",
        textAlign: "center",
    };
    return (
        <div style={errorStyle}>
            {error.message === "Member not found" ? formatMessage("Invisible to company") : error.message}
        </div>
    );
};

class Items extends PureComponent {
    static propTypes = {
        activeBrand: PropTypes.object,
        toolbarOpen: PropTypes.bool,
        scope: PropTypes.object.isRequired,
        productSchema: PropTypes.object,
        assortmentView: PropTypes.bool,
        onEntriesRefresh: PropTypes.func,
        bulkDragInsert: PropTypes.bool,
        bulkDragReorder: PropTypes.bool,
        refreshingFilters: PropTypes.bool,
        onFiltersRefreshComplete: PropTypes.func,
        useAutocompleteFilterURLQuery: PropTypes.bool,
        onFetchDeliveryWindows: PropTypes.func.isRequired,
        deliveryWindows: PropTypes.array.isRequired,
        fetchingWindows: PropTypes.bool.isRequired,
        isInsightsSidePanelOpen: PropTypes.bool,
        ldFlags: PropTypes.object,
        bulkManager: PropTypes.object,
    };

    styles = merge(
        $(" > .itemsLayout", {
            display: "flex",
            overflowX: "hidden",
        }),
        $(".isMobile & .sticky", {
            left: "0px !important",
        }),
        $(".isMenuPinned & .sticky", {
            left: `${theme.nav.iconAndTextWidth}px !important`,
        }),
        $(" .itemsSidebar", {
            flexGrow: 0,
            flexShrink: 0,
            width: "0px",
            overflow: "hidden",
            opacity: 0,
            paddingBottom: "80px",
            transition: "all 0.2s ease-out",
        }),
        $(".open .itemsSidebar", {
            width: "212px",
            opacity: 1,
        }),
        $(" .itemsMainColumn", {
            flexGrow: 1,
            flexShrink: 1,
            marginRight: "-8px",
        }),
        $(".toolbarOpen .itemsMainColumn", {
            paddingBottom: "224px",
        }),
        $(" .itemsSidebar .stickyFilter", {
            position: "fixed",
            top: "116px",
            left: "68px",
            bottom: "0px",
            zIndex: 100,
            width: "210px",
            paddingBottom: "80px",
            overflowY: "auto",
            backgroundColor: theme.palette.canvasColor,
        }),
        $(".isMenuPinned & .itemsSidebar > .stickyFilter", {
            left: "248px",
        }),
        $(".isMobile & .itemsSidebar > .stickyFilter", {
            left: "8px",
        }),
        $(" .deliveryWindowContainer", {
            textAlign: "center",
            borderBottom: "3px solid",
            margin: "16px 0",
            position: "relative",
        }),
        $(" .deliveryWindowContainer dt", {
            fontSize: "14px",
            fontWeight: "inherit",
        }),
        $(" .deliveryWindowContainer dt:after", {
            content: ":",
        }),
        $(" .deliveryWindowContainer dd", {
            fontSize: "24px",
            fontWeight: "bold",
        }),
        $(" .outOfRange dt", {
            opacity: "0.4",
        }),
        $(" .outOfRange dd", {
            opacity: "0.4",
        }),
        $(" .noResultsHeader", {
            padding: "12px 0 0",
            height: "200px",
            fontSize: "20px",
            textAlign: "center",
        }),
        $(" .noResultsSubheader", {
            display: "block",
            fontSize: "14px",
        })
    );

    muiStyles = {
        datepickerIcon: {
            position: "relative",
            left: "-10px",
            top: "2px",
        },
    };

    constructor(props) {
        super(props);
        this.state = {
            isSticky: false,
            minHeight: 0,
            isFiltersOpen: props.app.viewport !== VIEWPORT_PHABLET && props.app.viewport !== VIEWPORT_MOBILE,
            offsetY: undefined,
            reloadFilters: false,
        };

        this.handleToggleFilters = this.handleToggleFilters.bind(this);
        this.handleStickyStateChange = this.handleStickyStateChange.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (this.state.offsetY && !prevProps.isFetching && this.props.isFetching && this.props.results.length === 0) {
            window.scrollTo(0, this.state.offsetY);
        }
        // Such an interesting hack doing 2 consecutive component state updates to rerender and refresh filters
        // TODO: Make the data change instead
        if (
            this.props.catalog?.id !== prevProps.catalog?.id ||
            (this.props.type === "catalog" && this.props.scope?.viewAs !== prevProps.scope?.viewAs)
        ) {
            this.setState({ reloadFilters: true });
        }
        if (this.state.reloadFilters && this.props.catalog?.id === prevProps.catalog?.id) {
            this.setState({ reloadFilters: false });
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.app.viewport !== this.props.app.viewport) {
            this.setState({
                isFiltersOpen:
                    nextProps.app.viewport !== VIEWPORT_PHABLET && nextProps.app.viewport !== VIEWPORT_MOBILE,
            });
        }
    }

    handleStickyStateChange(isSticky) {
        const top =
            (window.pageYOffset || document.documentElement.scrollTop) - (document.documentElement.clientTop || 0);
        this.setState({
            isSticky,
            minHeight: isSticky ? window.innerHeight - 64 : 0,
            offsetY: isSticky ? top : undefined,
        });
    }

    handleToggleFilters() {
        this.setState({ isFiltersOpen: !this.state.isFiltersOpen });
    }

    handleEnterDeliveryDateLabel = () => {
        this.setState({ showDeliveryToolip: true });
    };

    handleLeaveDeliveryDateLabel = () => {
        this.setState({ showDeliveryToolip: false });
    };

    noContentRenderer = () => {
        const { router, catalog, scope, filters } = this.props;

        if (filters.size > 0) {
            return null;
        }

        let header;
        let subheader;

        if (router.location.pathname.includes("gallery")) {
            const canCreateProduct = me.hasPermission("create_new_product");
            header = canCreateProduct
                ? formatMessage("Add Products to the Gallery")
                : formatMessage("No products found");
            subheader = formatMessage("There are no products in this Gallery");
        } else {
            const isEditable = (catalog?._editable && !catalog?.isProcessing?.()) || false;

            if (router.location.pathname.includes("linesheet")) {
                const linesheetsAreCatalogs = scope.brand && scope.brand.hasPreference("linesheets_are_catalogs");
                header = isEditable
                    ? formatMessage("Add Products to this linesheet", {
                          linesheet: linesheetsAreCatalogs ? formatMessage("Catalog") : formatMessage("Linesheet"),
                      })
                    : formatMessage("No products found");
                subheader = isEditable
                    ? formatMessage(
                          "Click Import at the top of the screen or the Add Items button in the bottom left corner."
                      )
                    : formatMessage("View another linesheet in the gallery", {
                          linesheet: linesheetsAreCatalogs ? formatMessage("catalog") : formatMessage("linesheet"),
                          gallery: linesheetsAreCatalogs
                              ? formatMessage("Catalog Gallery")
                              : formatMessage("Linesheet Gallery"),
                      });
            } else if (router.location.pathname.includes("list")) {
                header = isEditable
                    ? formatMessage("Add Products to this Custom List")
                    : formatMessage("No products found");
                subheader = isEditable
                    ? formatMessage("Click the Add Items button in the bottom left corner.")
                    : formatMessage("View another custom list in the Custom List Gallery");
            }
        }
        return (
            <div className="noResultsHeader">
                {header}
                <span className="noResultsSubheader">{subheader}</span>
            </div>
        );
    };

    render() {
        const {
            activeBrand,
            activeList,
            app,
            bulkItems,
            cart,
            catalog,
            dragging,
            draggingEntryId,
            editMode,
            editNoteForEntryId,
            entities,
            error,
            filters,
            hasMoreResults,
            isFetching,
            isBulkDeleting,
            items,
            onActiveListAdd,
            onActiveListFetchEntries,
            onActiveListRemove,
            onAddToSelectedItems,
            onClear,
            onDeleteEntry,
            onDeleteSelectedEntries,
            onDrag,
            onDrop,
            onFetchNextPage,
            onFilterChange,
            onInventoryFilterChange,
            onItemClick,
            onItemMoreInfo,
            onRemoveFromSelectedItems,
            onReorderEntries,
            onRequestNoteDialogClose,
            onSelectAll,
            onSetActiveList,
            onSortChange,
            onUpdateEntry,
            onViewChange,
            onViewNotesDialog,
            onViewNotesToggle,
            pageSize,
            previewItem,
            productSchema,
            results,
            router,
            onSnackbarAdd,
            scope,
            sortBy,
            sortOrder,
            templates,
            atsCustomTemplates,
            toolbarOpen,
            type,
            view,
            viewDef,
            viewDefs,
            viewNotes,
            filterExclusions,
            filterInclusions,
            assortmentView,
            onEntriesRefresh,
            filtersGroups,
            bulkDragInsert,
            bulkDragReorder,
            onFiltersRefreshComplete,
            refreshingFilters,
            useAutocompleteFilterURLQuery,
            ldFlags,
            deliveryWindows,
            fetchingWindows,
            onFetchDeliveryWindows,
            isInsightsSidePanelOpen,
            bulkManager,
        } = this.props;

        const { isSticky, minHeight, isFiltersOpen, reloadFilters } = this.state;

        const circularProgressStyle = {
            position: "absolute",
            top: "50%",
            left: "0px",
            right: "0px",
            transform: "translateY(-50%)",
            textAlign: "center",
            zIndex: 10,
        };

        const showBulkManager =
            !!ldFlags?.addProductToLinesheetModalNu67306 &&
            bulkManager?.open &&
            Object.values(bulkManager?.processes)?.some((process) => process?.brandId === scope?.brand?._id);

        const prebookLabel = catalog && catalog.prebook ? ` (${formatMessage("Prebook")})` : "";
        const fields = productSchema.fields.toList().toJS();
        const advancedSearchFields = fields.filter((field) => {
            return field.filterable && field.unique && field.key.includes("sizes.") === false;
        });
        let advancedSearchKey = null;
        if (advancedSearchFields.length) {
            advancedSearchKey = advancedSearchFields[0];
        }

        const minDW = scope.brand.preferences.get("minimum_delivery_window");
        const isExpiredCatalog = catalog && catalog.isExpired(minDW);

        const isEditable = (catalog?._editable && !catalog?.isProcessing?.()) || false;

        // merge the sticky filters styles here the right position has to be calculated based on props
        const rightPosition =
            (this.props.app.sidePanelOpen ? theme.sidebar.width : 0) +
            (isInsightsSidePanelOpen ? theme.insightsSidePanel.width : 0) +
            (isInsightsSidePanelOpen || this.props.app.sidePanelOpen ? 8 : 0);

        const styles = merge(
            this.styles,
            $(" .sticky", {
                background: "white",
                borderTop: "1px solid #f0f0f0",
                boxShadow: "0 4px 4px 0 rgba(50,50,50,.08)",
                left: "60px !important",
                // if the product details side panel and/or the insights side panel are open, we need to push the sticky filters to the left
                right: `${rightPosition}px !important`,
                top: "60px !important",
                width: "auto !important",
                zIndex: 1000,
            })
        );

        return (
            <StickyContainer
                {...styles}
                className={classNames({
                    open: isFiltersOpen,
                    isPanelOpen: !!previewItem.current,
                    toolbarOpen,
                })}
            >
                {!dragging && !assortmentView && (
                    <Sticky topOffset={-68} style={dragging ? { borderTop: "none" } : undefined}>
                        {({ isSticky }) => {
                            return (
                                <ItemsHeader
                                    editable={isEditable}
                                    filters={filters}
                                    isFiltersOpen={isFiltersOpen}
                                    onFilterChange={onFilterChange}
                                    onToggleFiltersOpen={this.handleToggleFilters}
                                    onSortChange={onSortChange}
                                    onViewChange={onViewChange}
                                    onViewNotesToggle={onViewNotesToggle}
                                    router={router}
                                    sortBy={sortBy}
                                    sortOrder={sortOrder}
                                    type={type}
                                    view={view}
                                    viewNotes={viewNotes}
                                    viewport={app.viewport}
                                    advancedSearchKey={advancedSearchKey}
                                    isSticky={isSticky}
                                    onStickyStateChange={this.handleStickyStateChange}
                                    onSnackbarAdd={onSnackbarAdd}
                                    scope={scope}
                                    hasResults={results && results.length}
                                />
                            );
                        }}
                    </Sticky>
                )}
                {catalog && catalog._deliveryWindowLabel && (
                    <div
                        className={classNames({ deliveryWindowContainer: true, outOfRange: catalog.__out_of_range })}
                        data-test={DATATEST.CATALOG.DELIVERY_LABEL}
                        onMouseEnter={isExpiredCatalog ? this.handleEnterDeliveryDateLabel : null}
                        onMouseLeave={isExpiredCatalog ? this.handleLeaveDeliveryDateLabel : null}
                    >
                        <dl>
                            <dt>{formatMessage("Delivery")}</dt>
                            <dd>
                                <DatepickerIcon color={theme.palette.blue} style={this.muiStyles.datepickerIcon} />
                                {catalog._deliveryWindowLabel}
                                {prebookLabel}
                            </dd>
                            {isExpiredCatalog && (
                                <div style={{ position: "relative", maxWidth: "540px", margin: "0 auto" }}>
                                    <Tooltip
                                        label={formatMessage(
                                            "The preferred delivery has expired, but you may still shop items"
                                        )}
                                        data-test={DATATEST.CATALOG.DELIVERY_LABEL_TOOLTIP}
                                        show={this.state.showDeliveryToolip}
                                        touch={true}
                                        horizontalPosition="left"
                                        verticalPosition="top"
                                    />
                                </div>
                            )}
                        </dl>
                    </div>
                )}

                <div className="itemsLayout" style={{ minHeight: minHeight + "px" }}>
                    {!assortmentView && !reloadFilters ? (
                        <div data-test={DATATEST.FILTERS_SIDEBAR} className="itemsSidebar">
                            <div className={classNames({ stickyFilter: isSticky })}>
                                <Filters
                                    resource="item"
                                    useChips={
                                        type === "catalog" && scope?.brand?.hasPreference("adaptive_dynamic_filters")
                                    }
                                    activeFiltersValues={filters}
                                    filtersGroups={filtersGroups}
                                    onFilterChange={onFilterChange}
                                    onInventoryFilterChange={onInventoryFilterChange}
                                    scope={scope}
                                    clearAll={true}
                                    filterExclusions={filterExclusions}
                                    filterInclusions={filterInclusions}
                                    router={router}
                                    catalog={catalog}
                                    refreshingFilters={refreshingFilters}
                                    onFiltersRefreshComplete={onFiltersRefreshComplete}
                                    useAutocompleteURLQuery={useAutocompleteFilterURLQuery}
                                />
                            </div>
                        </div>
                    ) : null}
                    <div className="itemsMainColumn" data-test={DATATEST.ITEMS_GRID}>
                        {error && <ErrorMessage error={error} />}
                        {isBulkDeleting && (
                            <div style={circularProgressStyle}>
                                <CircularProgress />
                            </div>
                        )}
                        {isResultEmpty(results, isFetching, filters) ? (
                            <EmptyResults searchTerm={filters.get("__search") || ""} filters={filters} type={type} />
                        ) : (
                            <>
                                {!error &&
                                    !isBulkDeleting &&
                                    ["card", "tile", "largeTile", "panel", "assortment"].includes(view) && (
                                        <ItemGrid
                                            activeBrand={activeBrand}
                                            activeItemId={previewItem.current && previewItem.current._id}
                                            activeList={activeList}
                                            app={app}
                                            bulkItems={bulkItems}
                                            cart={cart}
                                            dragging={dragging}
                                            draggingEntryId={draggingEntryId}
                                            editable={isEditable}
                                            editMode={editMode}
                                            catalog={catalog}
                                            entities={entities}
                                            hasMoreResults={hasMoreResults}
                                            isActiveList={
                                                activeList.current && catalog && catalog.id === activeList.current.id
                                                    ? true
                                                    : false
                                            }
                                            isFetching={isFetching}
                                            items={items}
                                            onActiveListAdd={onActiveListAdd}
                                            onActiveListFetchEntries={onActiveListFetchEntries}
                                            onActiveListRemove={onActiveListRemove}
                                            onAddToSelectedItems={onAddToSelectedItems}
                                            onDeleteEntry={onDeleteEntry}
                                            onDrag={onDrag}
                                            onDrop={onDrop}
                                            onSnackbarAdd={onSnackbarAdd}
                                            onFetchNextPage={onFetchNextPage}
                                            onItemClick={onItemClick}
                                            onItemMoreInfo={onItemMoreInfo}
                                            onRemoveFromSelectedItems={onRemoveFromSelectedItems}
                                            onReorderEntries={onReorderEntries}
                                            onSetActiveList={onSetActiveList}
                                            onUpdateEntry={onUpdateEntry}
                                            onViewNotes={onViewNotesDialog}
                                            pageSize={pageSize}
                                            results={results}
                                            type={type}
                                            view={view}
                                            viewDef={viewDef}
                                            viewDefs={viewDefs}
                                            viewNotes={viewNotes}
                                            scope={scope}
                                            onEntriesRefresh={onEntriesRefresh}
                                            bulkDragInsert={bulkDragInsert}
                                            bulkDragReorder={bulkDragReorder}
                                            noContentRenderer={this.noContentRenderer}
                                            toolbarMinimized={!toolbarOpen}
                                            ldFlags={ldFlags}
                                        />
                                    )}
                                {!error && !isBulkDeleting && view === "list" && (
                                    <ItemsList
                                        activeBrand={activeBrand}
                                        activeItemId={previewItem.current && previewItem.current._id}
                                        activeList={activeList}
                                        app={app}
                                        bulkItems={bulkItems}
                                        cart={cart}
                                        dragging={dragging}
                                        draggingEntryId={draggingEntryId}
                                        editable={isEditable}
                                        catalog={catalog}
                                        entities={entities}
                                        hasMoreResults={hasMoreResults}
                                        isActiveList={
                                            activeList.current && catalog && catalog.id === activeList.current.id
                                                ? true
                                                : false
                                        }
                                        isFetching={isFetching}
                                        items={items}
                                        list={true}
                                        onActiveListAdd={onActiveListAdd}
                                        onActiveListFetchEntries={onActiveListFetchEntries}
                                        onActiveListRemove={onActiveListRemove}
                                        onAddToSelectedItems={onAddToSelectedItems}
                                        onDeleteEntry={onDeleteEntry}
                                        onDrag={onDrag}
                                        onDrop={onDrop}
                                        onSnackbarAdd={onSnackbarAdd}
                                        onFetchNextPage={onFetchNextPage}
                                        onItemClick={onItemClick}
                                        onItemMoreInfo={onItemMoreInfo}
                                        onRemoveFromSelectedItems={onRemoveFromSelectedItems}
                                        onReorderEntries={onReorderEntries}
                                        onSetActiveList={onSetActiveList}
                                        onUpdateEntry={onUpdateEntry}
                                        onViewNotes={onViewNotesDialog}
                                        pageSize={pageSize}
                                        results={results}
                                        type={type}
                                        view={view}
                                        viewDef={viewDef}
                                        viewDefs={viewDefs}
                                        viewNotes={viewNotes}
                                        scope={scope}
                                        onEntriesRefresh={onEntriesRefresh}
                                        bulkDragInsert={bulkDragInsert}
                                        bulkDragReorder={bulkDragReorder}
                                        noContentRenderer={this.noContentRenderer}
                                        toolbarMinimized={!toolbarOpen}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>

                {catalog && (
                    <NotesDialog
                        entities={entities}
                        catalogEntryId={editNoteForEntryId}
                        editable={isEditable}
                        onRequestClose={onRequestNoteDialogClose}
                        onUpdate={onUpdateEntry}
                        scope={scope}
                    />
                )}

                {bulkItems.size > 0 && !assortmentView && (
                    <BulkActions
                        activeList={activeList}
                        activeBrand={activeBrand}
                        bulkItems={bulkItems}
                        catalog={catalog}
                        filters={filters}
                        entities={entities}
                        onActiveListAdd={onActiveListAdd}
                        onActiveListFetchEntries={onActiveListFetchEntries}
                        onActiveListRemove={onActiveListRemove}
                        onClear={onClear}
                        onDeleteSelectedEntries={onDeleteSelectedEntries}
                        onSelectAll={onSelectAll}
                        onSetActiveList={onSetActiveList}
                        sidePanelOpen={app.sidePanelOpen}
                        onSnackbarAdd={onSnackbarAdd}
                        scope={scope}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        templates={templates}
                        atsCustomTemplates={atsCustomTemplates}
                        type={type}
                        viewAs={scope.viewAs}
                        viewAsGuest={scope.viewAsGuest}
                        viewport={app.viewport}
                        deliveryWindows={deliveryWindows}
                        fetchingWindows={fetchingWindows}
                        onFetchDeliveryWindows={onFetchDeliveryWindows}
                    />
                )}

                {showBulkManager && <BulkManager view={type} />}
            </StickyContainer>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        bulkManager: state.bulkManager,
        ldFlags: state.ldFlags,
    };
};

export default connect(mapStateToProps)(Items);
